/**
 * SearchBar Component
 * Main search interface with position indicator and character counter
 */

import { useState, useEffect, useRef } from 'react';
import { getOptimalSearchPosition } from '@/lib/search/positionAnalyzer';
import { PositionAnalysis } from '@/types/search';

interface SearchBarProps {
  onSearch: (query: string) => void;
  placeholder?: string;
  initialValue?: string;
}

export function SearchBar({ onSearch, placeholder = 'Search words...', initialValue = '' }: SearchBarProps) {
  const [query, setQuery] = useState(initialValue);
  const [analysis, setAnalysis] = useState<PositionAnalysis | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const searchTimeoutRef = useRef<number | null>(null);

  // Update local query state when initialValue (URL) changes
  // but DO NOT trigger a new search
  useEffect(() => {
    if (initialValue !== query) {
      setQuery(initialValue);
    }
  }, [initialValue]);

  // Update analysis when query changes
  useEffect(() => {
    if (query.length > 0) {
      const result = getOptimalSearchPosition(query);
      setAnalysis(result);
    } else {
      setAnalysis(null);
    }
  }, [query]);

  const triggerSearch = (value: string) => {
    if (searchTimeoutRef.current) {
      window.clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = window.setTimeout(() => {
      onSearch(value);
    }, 300);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    triggerSearch(value);
  };

  const handleClear = () => {
    setQuery('');
    if (searchTimeoutRef.current) {
      window.clearTimeout(searchTimeoutRef.current);
    }
    onSearch('');
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Search Input Container */}
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className={`
            w-full px-4 py-3 pr-24 text-lg
            border-2 rounded-lg
            bg-white text-gray-900
            placeholder:text-gray-400
            transition-all duration-200
            ${isFocused ? 'border-dictionary-accent shadow-lg' : 'border-gray-300'}
            focus:outline-none
            dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600
          `}
        />

        {/* Character Counter */}
        <div className="absolute right-12 top-1/2 -translate-y-1/2 text-sm text-gray-400">
          {query.length > 0 && `${query.length}`}
        </div>

        {/* Clear Button */}
        {query.length > 0 && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
            aria-label="Clear search"
          >
            <svg
              className="w-5 h-5 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Position Indicator */}
      {analysis && query.length > 0 && (
        <div className="mt-3 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg
              className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">
                {analysis.reason}
              </p>
              {analysis.suggestions.length > 0 && (
                <p className="text-xs text-blue-700 mt-1">
                  {analysis.suggestions[0]}
                </p>
              )}
            </div>
            {/* Position Badge */}
            <span className="px-2 py-1 text-xs font-bold bg-blue-600 text-white rounded">
              Position {analysis.optimalPosition}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
